import { useMemo, useEffect, useState } from 'react';
import { MessageType } from 'models/Message';

const MINUTE_IN_MS = 60 * 1000;

type UseDeleteMessageProps = Pick<MessageType, 'createdAt' | 'seenAt'> & {
  deleteWithinMinutes?: number;
  deleteWithinHoursIfNotSeen?: number;
};

export default function useDeleteMessage({
  createdAt,
  seenAt,
  deleteWithinMinutes = 1,
  deleteWithinHoursIfNotSeen = 6,
}: UseDeleteMessageProps) {
  const [currentTime, setCurrentTime] = useState(Date.now());

  const canDelete = useMemo(() => {
    if (!createdAt) return false;

    const now = new Date(currentTime);
    const createdAtDate = new Date(createdAt);
    const timeDiffInMinutes =
      (now.getTime() - createdAtDate.getTime()) / MINUTE_IN_MS;

    // Rule 1: Messages can be deleted within X minute of being sent (regardless of read status)
    if (timeDiffInMinutes <= deleteWithinMinutes) return true;

    // Rule 2: Messages can be deleted within X hours, but only if the recipient has not seen the message
    if (seenAt) return false;

    const timeDiffInHours = timeDiffInMinutes / 60;

    return timeDiffInHours <= deleteWithinHoursIfNotSeen;
  }, [
    createdAt,
    seenAt,
    currentTime,
    deleteWithinMinutes,
    deleteWithinHoursIfNotSeen,
  ]);

  useEffect(() => {
    if (!createdAt) return;

    const createdAtTime = new Date(createdAt).getTime();
    const timeSinceCreation = Date.now() - createdAtTime;
    const deleteWithinMs = deleteWithinMinutes * MINUTE_IN_MS;

    // Only run interval if we're within the first X minutes
    if (timeSinceCreation < deleteWithinMs) {
      const interval = setInterval(() => {
        const elapsed = Date.now() - createdAtTime;

        if (elapsed >= deleteWithinMs) {
          clearInterval(interval);
        }

        setCurrentTime(Date.now());
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [createdAt, deleteWithinMinutes]);

  return canDelete;
}
